import { memo, useState } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { Activity, HardDrive, Wifi, DatabaseZap, Disc, Zap } from 'lucide-react';
import DBTypeIcon from '../../components/DBTypeIcon';

export interface MetricTileItem {
    key?: string;
    label?: string;
    suffix?: string;
    digits?: number;
    value?: any;
}

export interface DBNodeData {
    nodeId?: string;
    dbType?: string;
    role?: string;
    displayRole?: string;
    display_role?: string;
    isElected?: boolean;
    upstreamLabel?: string;
    state?: string;
    host?: string;
    port?: number | string;
    tps?: number;
    qps?: number;
    connectionsUtilPct?: number;
    connectionsTotal?: number;
    connectionsMax?: number;
    cacheHitPct?: number;
    indexHitPct?: number;
    dbSizeMb?: number;
    databaseCount?: number;
    lagSec?: number;
    slowQueries?: number;
    isPrimary?: boolean;
    isSelected?: boolean;
    metricTiles?: MetricTileItem[];
    metric_tiles?: MetricTileItem[];
}

// DB size display helper
export const formatDbSize = (mb?: number) => {
    if (mb == null) return null;
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
    if (mb > 0 && mb < 0.01) return `<0.01 MB`;
    if (mb >= 0.01 && mb < 1) return `${mb.toFixed(2)} MB`;
    return `${mb.toFixed(0)} MB`;
};

// Formatter for dynamic metric tile values
export const formatTileValue = (tile: MetricTileItem) => {
    if (tile.value == null || tile.value === '') return '—';
    if (typeof tile.value !== 'number' || isNaN(tile.value)) {
        return `${tile.value}${tile.suffix || ''}`;
    }

    const val = tile.value;
    const rawSuffix = tile.suffix || '';
    const cleanSuffix = rawSuffix.trim();

    // Special handling for DB / Memory size in MB
    if (tile.key?.includes('size_mb') || cleanSuffix.toLowerCase() === 'mb') {
        if (val >= 1024) return `${(val / 1024).toFixed(1)} GB`;
        if (val > 0 && val < 0.01) return `<0.01 MB`;
        if (val >= 0.01 && val < 1) return `${val.toFixed(2)} MB`;
        const d = tile.digits ?? 0;
        return `${d > 0 ? val.toFixed(d) : Math.round(val)} MB`;
    }

    const digits = tile.digits != null ? tile.digits : (Number.isInteger(val) ? 0 : 1);
    const formattedNum = Number(val.toFixed(digits)).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
    });

    const suffixToUse = rawSuffix.startsWith(' ') || rawSuffix.startsWith('%') ? rawSuffix : (rawSuffix ? ` ${rawSuffix}` : '');
    return `${formattedNum}${suffixToUse}`;
};

// Icon & palette matcher for dynamic metric tiles
export const getTileStyle = (key?: string, label?: string, index: number = 0, isOffline: boolean = false) => {
    if (isOffline) {
        return {
            containerClass: 'bg-[#220c10] border-red-900/40 text-red-300',
            iconClass: 'text-red-400',
            icon: Activity,
        };
    }

    const lowerKey = (key || '').toLowerCase();
    const lowerLabel = (label || '').toLowerCase();

    // TPS / QPS / Throughput
    if (lowerKey.includes('tps') || lowerKey.includes('qps') || lowerLabel.includes('tps') || lowerLabel.includes('qps')) {
        return {
            containerClass: 'bg-[#0d1c38] border-[#1d3866] text-blue-300',
            iconClass: 'text-blue-400',
            icon: Activity,
        };
    }

    // Disk / Storage
    if (lowerKey.includes('disk') || lowerLabel.includes('disk')) {
        return {
            containerClass: 'bg-[#261609] border-[#543011] text-amber-300',
            iconClass: 'text-amber-400',
            icon: Disc,
        };
    }

    // DB Size / Memory
    if (lowerKey.includes('size') || lowerLabel.includes('size') || lowerKey.includes('memory')) {
        return {
            containerClass: 'bg-[#1c1333] border-[#3b2366] text-purple-300',
            iconClass: 'text-purple-400',
            icon: HardDrive,
        };
    }

    // Connections
    if (lowerKey.includes('conn') || lowerLabel.includes('conn')) {
        return {
            containerClass: 'bg-[#0a2334] border-[#154666] text-cyan-300',
            iconClass: 'text-cyan-400',
            icon: Wifi,
        };
    }

    // Cache / Buffer
    if (lowerKey.includes('cache') || lowerLabel.includes('cache') || lowerKey.includes('buffer')) {
        return {
            containerClass: 'bg-[#092823] border-[#135246] text-emerald-300',
            iconClass: 'text-emerald-400',
            icon: DatabaseZap,
        };
    }

    // Active Queries / Queries
    if (lowerKey.includes('quer') || lowerLabel.includes('quer')) {
        return {
            containerClass: 'bg-[#0a2628] border-[#134e4a] text-teal-300',
            iconClass: 'text-teal-400',
            icon: Zap,
        };
    }

    // Fallback palette by index
    const fallbacks = [
        { containerClass: 'bg-[#0d1c38] border-[#1d3866] text-blue-300', iconClass: 'text-blue-400', icon: Activity },
        { containerClass: 'bg-[#1c1333] border-[#3b2366] text-purple-300', iconClass: 'text-purple-400', icon: HardDrive },
        { containerClass: 'bg-[#0a2334] border-[#154666] text-cyan-300', iconClass: 'text-cyan-400', icon: Wifi },
        { containerClass: 'bg-[#092823] border-[#135246] text-emerald-300', iconClass: 'text-emerald-400', icon: DatabaseZap },
    ];

    return fallbacks[index % fallbacks.length];
};

const DBNode = memo(({ id, data }: NodeProps<DBNodeData>) => {
    const {
        dbType,
        role,
        displayRole,
        display_role,
        state,
        host,
        port,
        tps,
        qps,
        connectionsUtilPct,
        cacheHitPct,
        dbSizeMb,
        isPrimary,
        isSelected,
        metricTiles,
        metric_tiles,
    } = data;

    const [isHovered, setIsHovered] = useState(false);

    const isOnline = state?.toLowerCase() === 'online' || state?.toLowerCase() === 'healthy';
    const isDegraded = state?.toLowerCase() === 'degraded';
    const isOffline = !isOnline && !isDegraded;

    const activeDisplayRole = displayRole || display_role || role || 'NODE';
    const isWasPrimary = activeDisplayRole.toLowerCase() === 'was primary';

    const isPrimaryRole = !isWasPrimary && (isPrimary || role?.toLowerCase() === 'primary' || role?.toLowerCase() === 'standalone');

    const handleStyle = `!w-2.5 !h-2.5 !bg-slate-300 !border-2 !border-[#070b16] !rounded-full transition-all duration-200 hover:scale-125 hover:!bg-cyan-400 hover:!border-cyan-200`;

    // Border & Glow styling
    const borderStyle = isOffline
        ? 'border-red-500/80 bg-[#16080a] shadow-[0_0_20px_rgba(239,68,68,0.35)]'
        : isSelected
            ? isPrimaryRole
                ? 'border-amber-400 ring-2 ring-amber-400/60 ring-offset-2 ring-offset-[#090e1a] shadow-[0_0_25px_rgba(245,158,11,0.45)]'
                : 'border-blue-400 ring-2 ring-blue-400/60 ring-offset-2 ring-offset-[#090e1a] shadow-[0_0_25px_rgba(59,130,246,0.45)]'
            : isHovered
                ? isPrimaryRole
                    ? 'border-amber-400 scale-[1.02] shadow-[0_0_20px_rgba(245,158,11,0.3)]'
                    : 'border-blue-400 scale-[1.02] shadow-[0_0_20px_rgba(59,130,246,0.3)]'
                : isPrimaryRole
                    ? 'border-amber-500/60 shadow-[0_0_12px_rgba(245,158,11,0.15)] bg-[#090e1a]'
                    : 'border-blue-500/60 shadow-[0_0_12px_rgba(59,130,246,0.15)] bg-[#090e1a]';

    // Header gradient
    const headerBg = isOffline
        ? 'bg-gradient-to-b from-[#360e13] to-[#1d070a] border-red-500/50'
        : isPrimaryRole
            ? 'bg-gradient-to-b from-[#2e1d0d] to-[#1c1208] border-amber-500/40'
            : 'bg-gradient-to-b from-[#0e213e] to-[#081324] border-cyan-500/40';

    const displayTps = tps ?? qps;
    const tiles = (metricTiles && metricTiles.length > 0) ? metricTiles : metric_tiles;

    return (
        <div
            className="relative group select-none"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* RECTANGULAR COMPACT NODE CARD */}
            <div
                className={`
                    relative rounded-xl w-[236px] flex flex-col
                    backdrop-blur-2xl border transition-all duration-300 cursor-pointer overflow-hidden
                    ${borderStyle}
                `}
            >
                {/* HEADER CAP */}
                <div className={`relative px-2.5 pt-2.5 pb-2 border-b flex items-center justify-between gap-2 transition-colors duration-300 ${headerBg}`}>
                    <div className="flex items-center gap-2 min-w-0 z-10">
                        {/* Icon Container */}
                        <div
                            className={`w-6.5 h-6.5 rounded-lg border flex items-center justify-center shrink-0 shadow-sm ${isOffline
                                ? 'bg-red-500/20 border-red-500/40 text-red-400'
                                : isPrimaryRole
                                    ? 'bg-amber-500/20 border-amber-500/40 text-amber-300'
                                    : 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300'
                                }`}
                        >
                            <DBTypeIcon dbType={dbType} className="w-3.5 h-3.5 shrink-0" />
                        </div>
                        <div className="min-w-0 flex flex-col">
                            {/* Host / IP */}
                            <div className='flex gap-1'>
                                <div className="text-[11.5px] font-bold text-slate-100 font-mono tracking-tight truncate leading-tight" title={host || id}>
                                    {host || id}:{port || '5432'}
                                </div>
                            </div>
                            {/* Role Badge */}
                            <div className="flex items-center gap-1 mt-0.5">
                                <span
                                    className={`px-1 py-0.2 rounded text-[8px] font-extrabold uppercase tracking-wider border shadow-sm ${isOffline
                                        ? 'bg-red-500/25 text-red-200 border-red-500/45'
                                        : isWasPrimary
                                            ? 'bg-rose-500/25 text-rose-200 border-rose-500/45'
                                            : isPrimaryRole
                                                ? 'bg-amber-500/25 text-amber-200 border-amber-500/45'
                                                : 'bg-cyan-500/25 text-cyan-200 border-cyan-500/45'
                                        }`}
                                >
                                    {activeDisplayRole}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Status Pill */}
                    <div className="shrink-0 flex items-center z-10">
                        {isOnline ? (
                            <div className="flex items-center gap-1 bg-emerald-500/15 border border-emerald-500/30 px-1.5 py-0.5 rounded-full" title="Status: Online">
                                <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.8)]" />
                                </span>
                                <span className="text-[8px] font-extrabold text-emerald-300 uppercase tracking-wider">LIVE</span>
                            </div>
                        ) : isDegraded ? (
                            <div className="flex items-center gap-1 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-full" title="Status: Degraded">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.8)]" />
                                <span className="text-[8px] font-extrabold text-amber-300 uppercase tracking-wider">DEG</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1 bg-red-500/20 border border-red-500/40 px-1.5 py-0.5 rounded-full" title="Status: Offline">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.9)]" />
                                <span className="text-[8px] font-extrabold text-red-300 uppercase tracking-wider">OFF</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* STATS BODY */}
                <div className={`p-2 relative z-10 ${isOffline ? 'bg-[#18090b]' : 'bg-[#0a101f]'}`}>
                    <div className="grid grid-cols-2 gap-1.5">
                        {tiles && tiles.length > 0 ? (
                            tiles.map((tile, idx) => {
                                const style = getTileStyle(tile.key, tile.label, idx, isOffline);
                                const formattedVal = formatTileValue(tile);
                                const IconComponent = style.icon;
                                return (
                                    <div
                                        key={tile.key || idx}
                                        className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${style.containerClass}`}
                                        title={`${tile.label || tile.key}: ${formattedVal}`}
                                    >
                                        <div className="flex items-center justify-between gap-1 text-[8px] font-extrabold uppercase tracking-tight pl-0.5 min-w-0">
                                            <div className="flex items-center gap-1 min-w-0 truncate">
                                                <IconComponent className={`w-2.5 h-2.5 shrink-0 ${style.iconClass}`} />
                                                <span className="truncate">{tile.label || tile.key}:</span>
                                            </div>
                                            <span className="text-white font-mono shrink-0 ml-0.5">{formattedVal}</span>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <>
                                {/* TPS CARD */}
                                <div className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${isOffline ? 'bg-[#220c10] border-red-900/40 text-red-300' : 'bg-[#0d1c38] border-[#1d3866] text-blue-300'
                                    }`}>
                                    <div className="flex items-center gap-1 text-[8px] font-extrabold uppercase tracking-widest pl-0.5 truncate">
                                        <Activity className={`w-2.5 h-2.5 ${isOffline ? 'text-red-400' : 'text-blue-400'}`} />
                                        <span>TPS:</span>
                                        <span className='text-white'>{displayTps != null ? displayTps.toLocaleString() : '—'}</span>
                                    </div>
                                </div>

                                {/* DB SIZE CARD */}
                                <div className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${isOffline ? 'bg-[#220c10] border-red-900/40 text-red-300' : 'bg-[#1c1333] border-[#3b2366] text-purple-300'
                                    }`}>
                                    <div className="flex items-center gap-1 text-[8px] font-extrabold uppercase tracking-widest pl-0.5 truncate">
                                        <HardDrive className={`w-2.5 h-2.5 ${isOffline ? 'text-red-400' : 'text-purple-400'}`} />
                                        <span>Size:</span>
                                        <span className='text-white'>{formatDbSize(dbSizeMb) || '—'}</span>
                                    </div>
                                </div>

                                {/* CONN UTIL CARD */}
                                <div className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${isOffline ? 'bg-[#220c10] border-red-900/40 text-red-300' : 'bg-[#0a2334] border-[#154666] text-cyan-300'
                                    }`}>
                                    <div className="flex items-center gap-1 text-[8px] font-extrabold uppercase tracking-widest pl-0.5 truncate">
                                        <Wifi className={`w-2.5 h-2.5 ${isOffline ? 'text-red-400' : 'text-cyan-400'}`} />
                                        <span>Conn:</span>
                                        <span className='text-white'>{connectionsUtilPct != null ? `${Number(Number(connectionsUtilPct).toFixed(1))}%` : '—'}</span>
                                    </div>
                                </div>

                                {/* CACHE HIT CARD */}
                                <div className={`border rounded-lg p-1.5 flex flex-col justify-center relative overflow-hidden shadow-sm ${isOffline ? 'bg-[#220c10] border-red-900/40 text-red-300' : 'bg-[#092823] border-[#135246] text-emerald-300'
                                    }`}>
                                    <div className="flex items-center gap-1 text-[8px] font-extrabold uppercase tracking-widest pl-0.5 truncate">
                                        <DatabaseZap className={`w-2.5 h-2.5 ${isOffline ? 'text-red-400' : 'text-emerald-400'}`} />
                                        <span>Cache:</span>
                                        <span className='text-white'>{cacheHitPct != null ? `${cacheHitPct}%` : '—'}</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* TOP HANDLE */}
            <Handle
                type="target"
                position={Position.Top}
                id="top"
                className={`${handleStyle} ${!isPrimaryRole ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                style={{ top: -4 }}
            />

            {/* BOTTOM HANDLE */}
            <Handle
                type="source"
                position={Position.Bottom}
                id="bottom"
                className={`${handleStyle} ${isPrimaryRole ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                style={{ bottom: -4 }}
            />
        </div>
    );
});

DBNode.displayName = 'DBNode';
export default DBNode;





